import React, { ReactChild, ReactChildren, ReactNode } from 'react';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { AddressValue } from '../Location/Locations';
import {
  getPeriodText,
  parsePeriodValuesString,
  PeriodString,
} from '../SectionPeriod/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { formattedDate, toDate } from 'shared/utils/date';
import JSON5 from 'json5';

export const DESCRIPTION_QUESTION_ID = 1;
export const DATES_QUESTION_ID_1 = 10003;
export const DATES_QUESTION_ID_2 = 10004;
export const LOCATIONS_QUESTION_ID = 30017;
const ACTIVITIES_SHORT_QUESTION_TEXT: { [id: number]: string } = {
  110001: 'Primary delivery method',
  110002: 'Primarily deliver services',
  110003: 'Additional primarily deliver services',
  110004: 'Average value of the goods',
  110005: 'Average number of goods',
  110006: 'Average number of goods provided',
  110050: 'Additional information',
};

export const getActivitiesQuestionText = (
  questionId: number,
  fallback: string,
): string => ACTIVITIES_SHORT_QUESTION_TEXT[questionId] || fallback;

export const getActivitiesAnswers = (
  answers: Answer[] | undefined,
): { questionId: number; questionText: string; text: string }[] => {
  if (!answers) return [];
  return answers
    .filter(a => a.question.section === 'activities')
    .map(a => ({
      questionId: a.question.dbId,
      questionText: getActivitiesQuestionText(
        a.question.dbId,
        a.questionText || a.question.text || '',
      ),
      text: a.text || '',
    }));
};

export const getDescription = (answers: Answer[] | undefined): string => {
  return (
    answers?.find(x => x.question.dbId === DESCRIPTION_QUESTION_ID)?.text ||
    'No available'
  );
};

export const getLocations = (answers: Answer[] | undefined): AddressValue[] => {
  try {
    const locations = answers?.find(
      x => x.question.dbId === LOCATIONS_QUESTION_ID,
    );
    if (!locations) return [];
    const parsed = JSON5.parse(locations.text);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const getDatesAnnualPeriod = (answers: Answer[] | undefined): string => {
  return answers?.find(x => x.question.dbId === 10002)?.text || 'Unknown';
};

export const parseDatesPeriod = (text: string | PeriodString | undefined) => {
  if (!text) {
    return {
      startDate: 'Unknown',
      endDate: 'Unknown',
      card: 'customYear',
    };
  }
  if (typeof text === 'string') {
    return parsePeriodValuesString(text);
  }
  return {
    startDate: toDate(text.startDate),
    endDate: toDate(text.endDate),
    card: text.option,
  };
};

export const getDatesPeriod = (answers: Answer[] | undefined) => {
  const datesAnswer =
    answers?.find(x => x.question.dbId === DATES_QUESTION_ID_1) ||
    answers?.find(x => x.question.dbId === DATES_QUESTION_ID_2);

  return parseDatesPeriod(datesAnswer?.text);
};

export const getSectionAnswers = (
  id: FeedbackSectionId,
  answers: Answer[],
): JSX.Element | ReactChild | ReactChildren | ReactNode => {
  switch (id) {
    case 'description':
      return getDescription(answers);
    case 'dates': {
      const dates = getDatesPeriod(answers);
      const period = getDatesAnnualPeriod(answers);
      return (
        <TrueImpactText>
          <strong>Reporting period type:</strong> {period}
          <br />
          <br />
          <strong>Time period:</strong> {getPeriodText(dates.card)}
          <br />
          <br />
          <strong>Program start:</strong> {formattedDate(dates.startDate)}
          <br />
          <br />
          <strong>Program end:</strong> {formattedDate(dates.endDate)}
        </TrueImpactText>
      );
    }
    case 'location': {
      const location = getLocations(answers);
      return location.map(l => (
        <TrueImpactText className="location">
          <strong>{l.address}</strong> {l.percent || l.percentage}%
        </TrueImpactText>
      ));
    }
    case 'activities': {
      const activities = getActivitiesAnswers(answers);
      if (activities.length === 0) return <>Not provided</>;
      return (
        <TrueImpactText>
          {activities.map((a, i) => (
            <React.Fragment key={i}>
              <strong>{a.questionText}:</strong> {a.text || 'Not provided'}
              {i < activities.length - 1 && (
                <>
                  <br />
                  <br />
                </>
              )}
            </React.Fragment>
          ))}
        </TrueImpactText>
      );
    }
    case 'budget':
    case 'finalize_report':
    case 'interim_report':
    case 'custom_questions':
    case 'impact_summary':
    case 'indicator_details':
    case 'narrative':
    case 'beneficiaries':
    case 'allocation':
    default:
      return <>Unknown summary</>;
  }
};
